<template>
    <ModalWrapper v-model="modal" max-width="500">
        <template v-slot:title>
            {{ $t('passport') }}
        </template>
        <template v-slot:content>
            <v-form v-model="valid" data-cy="passport-form">
                <v-row no-gutters class="align-center justify-space-between gc-4 mb-2">
                    <LocaleSwitcher @change="changeActiveLangCode" />
                    <v-switch
                        v-model="passport.active"
                        data-cy="passport-active"
                        color="primary"
                        hide-details
                        :label="passport.active ? $t('active') : $t('inactive')"
                    />
                </v-row>
                <div class="d-flex flex-column gr-2">
                    <v-select
                        v-model="passport.type"
                        :items="passportTypes"
                        :label="`${$t('document_type')}*`"
                        clearable
                        :rules="[requiredRule('document_type')]"
                        data-cy="passport-type"
                    />
                    <v-text-field
                        v-model="passport.number"
                        :label="`${$t('document_number')}*`"
                        :rules="[requiredRule('document_number')]"
                        data-cy="passport-number"
                    />
                    <TextFieldLocale
                        v-model="passport.issuePlace[activeLangCode]"
                        :label="$t('issue_place')"
                        :active-lang-code="activeLangCode"
                        data-cy="passport-place"
                    />
                    <date-picker
                        v-model="passport.issueDate"
                        :label="$t('issue_date')"
                        :max="today"
                        year-active-picker
                        color="primary"
                        data-cy="passport-issue-date"
                    />
                    <date-picker
                        v-model="passport.validTill"
                        :label="`${$t('expire_date')}*`"
                        :min="today"
                        year-active-picker
                        color="primary"
                        :rules="[requiredRule('expire_date')]"
                        data-cy="passport-expire-date"
                    />
                </div>
            </v-form>
        </template>
        <template v-slot:btns>
            <v-btn color="primary" variant="flat" data-cy="save" :disabled="isLoading || !valid" @click="save">
                {{ $t('save') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import {EventBus, SHOW_PASSPORT_MODAL_EVENT} from '@/utils/event-bus'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import {clean, clone} from '~/utils/helpers'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'

    @Component({
        components: {TextFieldLocale, DatePicker},
    })
    export class PassportModal extends mixins(FormValidationRulesMixin) {
        activeLangCode = 'en'
        valid: boolean = false
        personId = null
        passport = {
            id: null,
            type: null,
            number: null,
            issueDate: null,
            validTill: null,
            issuePlace: {},
            place: null,
            active: true,
        }
        modal: boolean = false
        isLoading: boolean = false
        type: string = null

        mounted() {
            EventBus.$on(SHOW_PASSPORT_MODAL_EVENT, this.showModal)
            this.activeLangCode = this.locale
        }

        beforeUnmount() {
            EventBus.$off(SHOW_PASSPORT_MODAL_EVENT, this.showModal)
        }

        showModal({type, passport, personId}) {
            this.type = type
            this.passport = passport
                ? clone(passport)
                : {
                      type: null,
                      number: null,
                      issueDate: null,
                      validTill: null,
                      issuePlace: {},
                      active: true,
                  }
            this.personId = personId ? personId : null
            this.modal = true
        }

        get locale(): string {
            return this.$i18n.locale
        }

        get passportTypes() {
            const docTypes = ['PASSPORT', 'PASSPORT_EXTERNAL', 'BIRTH_CERTIFICATE', 'OTHER']
            return docTypes.map(value => ({value, title: this.$t(`documentTypes.${value}`)}))
        }

        get today() {
            return new Date().toISOString()
        }

        async save() {
            this.isLoading = true
            const {type, number, issueDate, validTill, active, issuePlace} = this.passport,
                passport = {
                    type,
                    number,
                    issueDate,
                    issuePlace,
                    validTill,
                    active,
                }
            clean(passport)
            try {
                if (this.type === 'crmperson') {
                    if (this.passport.id) {
                        await this.$api.crmPersonPassport.put(this.personId, this.passport.id, passport)
                    } else {
                        await this.$api.crmPersonPassport.post(this.personId, passport)
                    }
                }
                if (this.type === 'person') {
                    if (this.passport.id) {
                        await this.$api.personPassport.put(this.personId, this.passport.id, passport)
                    } else {
                        await this.$api.personPassport.post(this.personId, passport)
                    }
                }
                this.$toast.success(this.$t('messages.success.passport_saved'))
                this.$emit('saved')
                this.modal = false
            } catch (e) {
                this.$toast.error(e.msg || e.errors[0].message)
            } finally {
                this.isLoading = false
            }
        }

        changeActiveLangCode(code: string) {
            this.activeLangCode = code
        }
    }

    export default PassportModal
</script>

<style scoped lang="scss">
    .modal-close-btn-block {
        position: relative;
    }
</style>
